import type { SalesforcePaymentsExpressConfig } from '../../types/config';
import type { PaymentIntentRequest } from '../../types/paymentRequest';
import type { PaymentMode, VendorAccount } from '../../types/paymentMethodSet';
import { PaymentMethodType } from '../../types/paymentMethodSet';
import { PaymentMethodElement } from '../../types/paymentMethodSet';
import './adyen-styles.css';
import type { VendorPaymentMethodElement } from '../../types/component';
import { SfpAdyenExpressComponent } from './sfp-adyen-express-component';
import { DEFAULT_LABELS } from '../../util/utility';
import type { SfPaymentMethodComponentConstructor } from '../sfp-payment-method-component';
import type { MetadataReader } from '../../util/metadata-reader';
import type { Component } from '../../components/component';

export class SfpAdyenExpressApplePay extends SfpAdyenExpressComponent {
  TYPE = PaymentMethodType.APPLEPAY;
  ELEMENT = PaymentMethodElement.ADYEN_EXPRESS_APPLEPAY;

  onRender: (types: PaymentMethodType[]) => void;

  generateConfiguration(
    config: SalesforcePaymentsExpressConfig,
    _paymentRequest: PaymentIntentRequest,
    _vendorAccount: VendorAccount
  ): any {
    const requiredBillingContactFields = ['name'];
    const requiredShippingContactFields = ['name'];
    if (config.options.billingAddressRequired ?? true) {
      requiredBillingContactFields.push('postalAddress');
    }
    if (config.options.phoneNumberRequired) {
      requiredShippingContactFields.push('phone');
    }
    if (config.options.emailAddressRequired) {
      requiredShippingContactFields.push('email');
    }
    if (config.options.shippingAddressRequired) {
      requiredShippingContactFields.push('postalAddress');
    }
    return {
      requiredBillingContactFields: requiredBillingContactFields,
      requiredShippingContactFields: requiredShippingContactFields,

      // Callbacks
      onShippingContactSelected: (resolve: any, reject: any, event: any): any => {
        const shippingAddress = event.shippingContact;
        const processedShippingAddress = {
          city: shippingAddress.locality,
          country: shippingAddress.countryCode,
          postal_code: shippingAddress.postalCode,
          state: shippingAddress.administrativeArea,
        };

        const callBackFunctionAddress = {
          updateShippingAddress: (updatedData: any): any => {
            if (updatedData.errors) {
              reject();
              return;
            }
            const shippingMethods: unknown[] = [];
            const lineItems: unknown[] = [];

            const amountToUpdate = String(updatedData.total);

            if (updatedData.shippingMethods && updatedData.shippingMethods.length > 0) {
              updatedData.shippingMethods.forEach((option: any) => {
                const shippingMethod: unknown = {
                  label: option.name,
                  detail: option.classOfService,
                  amount: option.amount,
                  identifier: option.id,
                };
                shippingMethods.push(shippingMethod);
              });
            }
            if (updatedData.lineItems && updatedData.lineItems.length > 0) {
              updatedData.lineItems.forEach((lineItem: any) => {
                const item: unknown = {
                  label: lineItem.name,
                  type: 'final',
                  amount: String(lineItem.amount),
                };
                lineItems.push(item);
              });
            }
            const totals = {
              label: config.labels?.finalAmountTitle ?? DEFAULT_LABELS.finalAmountTitle,
              type: 'final',
              amount: amountToUpdate,
            };

            const transactionInfo = {
              newShippingMethods: shippingMethods,
              newLineItems: lineItems,
              newTotal: totals,
            };
            try {
              // call event.resolve within 20 seconds
              resolve(transactionInfo);
            } catch (e) {
              //do nothing until shipping is handled
              console.log(e);
              reject();
            }
          },
        };

        config.actions.onShippingAddressChange(processedShippingAddress, callBackFunctionAddress);
      },

      onShippingMethodSelected: (resolve: any, reject: any, event: any): any => {
        const shippingMethod = event.shippingMethod;
        const processedShippingRate = {
          id: shippingMethod.identifier,
        };

        const callBackFunctionAddress = {
          updateShippingMethod: (updatedData: any): any => {
            const lineItems: unknown[] = [];

            const amountToUpdate = String(updatedData.total);

            if (updatedData.lineItems && updatedData.lineItems.length > 0) {
              updatedData.lineItems.forEach((lineItem: any) => {
                const item: unknown = {
                  label: lineItem.name,
                  type: 'final',
                  amount: String(lineItem.amount),
                };
                lineItems.push(item);
              });
            }
            const totals = {
              label: config.labels?.finalAmountTitle ?? DEFAULT_LABELS.finalAmountTitle,
              type: 'final',
              amount: amountToUpdate,
            };

            const transactionInfo = {
              newLineItems: lineItems,
              newTotal: totals,
            };
            try {
              // call event.resolve within 20 seconds
              resolve(transactionInfo);
            } catch (e) {
              //do nothing until shipping is handled
              console.log(e);
              reject();
            }
          },
        };

        config.actions.onShippingMethodChange(processedShippingRate, callBackFunctionAddress);
      },

      onClick: async (resolve: any): Promise<any> => {
        await config.actions.onClick(this.TYPE, this.paymentMode);
        resolve();
      },

      onError: (_error: any): any => {
        const errorEvent = new CustomEvent('sfp:paymentcancel', {
          bubbles: true,
          composed: true,
        });
        this.element.dispatchEvent(errorEvent);
      },

      onAuthorized: (resolve: any, reject: any, event: any): any => {
        const createName = (firstName: string, lastName: string): string => {
          if (firstName) {
            if (lastName) {
              return firstName + ' ' + lastName;
            }
            return firstName;
          }
          return lastName;
        };
        const paymentData = event.payment;
        this.billingDetails = {
          email: paymentData.shippingContact?.emailAddress,
          phone: paymentData.shippingContact?.phoneNumber,
          name: createName(paymentData.billingContact?.givenName, paymentData.billingContact?.familyName),
          address: {
            country: paymentData.billingContact?.countryCode,
            city: paymentData.billingContact?.locality,
            line1: paymentData.billingContact?.addressLines?.[0],
            line2: paymentData.billingContact?.addressLines?.[1],
            postalCode: paymentData.billingContact?.postalCode,
            state: paymentData.billingContact?.administrativeArea,
          },
        };

        this.shippingDetails = {
          name: createName(paymentData.shippingContact?.givenName, paymentData.shippingContact?.familyName),
          address: {
            country: paymentData.shippingContact?.countryCode,
            city: paymentData.shippingContact?.locality,
            line1: paymentData.shippingContact?.addressLines?.[0],
            line2: paymentData.shippingContact?.addressLines?.[1],
            postalCode: paymentData.shippingContact?.postalCode,
            state: paymentData.shippingContact?.administrativeArea,
          },
        };

        this.paymentResolve(true);
        try {
          // call event.resolve within 20 seconds
          resolve(0);
        } catch (e) {
          //do nothing until shipping is handled
          console.log(e);
          reject();
        }
      },

      buttonType: config.theme?.expressButtons?.buttonLabels?.applepay,
      buttonColor: config.theme?.expressButtons?.buttonColors?.applepay,
    };
  }
}

export const createSfpAdyenExpressApplePay: SfPaymentMethodComponentConstructor = function (
  parentComponent: Component,
  vendorObject: any,
  elements: any,
  metadataReader: MetadataReader,
  config: SalesforcePaymentsExpressConfig,
  paymentRequest: PaymentIntentRequest,
  paymentMethod: VendorPaymentMethodElement,
  paymentMode?: PaymentMode
) {
  return new SfpAdyenExpressApplePay(
    parentComponent,
    vendorObject,
    elements,
    metadataReader,
    config,
    paymentRequest,
    paymentMethod,
    paymentMode
  );
};
